import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { EmptyState } from '../components/states';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';

const len = (a: unknown[] | null | undefined): number | null =>
  Array.isArray(a) ? a.length : null;

/**
 * Routing domain landing page. Leads with real operational signal — open route-safety
 * findings and how many devices carry routing configuration — and launches into Route
 * Explorer. Permission-gated (routing:read) and fail-soft.
 */
export function RoutingOverview(): JSX.Element {
  const t = useT();
  const { can } = useAuth();
  const allowed = can('routing:read');

  const findings = useAsync(
    () => (allowed ? api.get<unknown[]>('/routing/safety/findings') : Promise.resolve(null)),
    [],
  );
  const configs = useAsync(
    () => (allowed ? api.get<unknown[]>('/routing/configs') : Promise.resolve(null)),
    [],
  );

  const findingCount = len(findings.data);
  const configCount = len(configs.data);

  if (!allowed) {
    return (
      <section>
        <div className="page-head">
          <h1>{t('nav.groups.routing')}</h1>
        </div>
        <EmptyState hint={t('states.permissionDenied')} />
      </section>
    );
  }

  return (
    <section>
      <div className="page-head">
        <h1>{t('nav.groups.routing')}</h1>
      </div>

      <div className="tiles">
        <Link className="tile card--link" to="/route-explorer">
          <span className="tile__label">{t('rte.findings')}</span>
          <span className="tile__value">{findingCount ?? '…'}</span>
          {findingCount && findingCount > 0 ? (
            <span className="chip chip--warning">{t('rte.needsReview')}</span>
          ) : null}
        </Link>
        <Link className="tile card--link" to="/route-explorer">
          <span className="tile__label">{t('rte.configured')}</span>
          <span className="tile__value">{configCount ?? '…'}</span>
        </Link>
      </div>

      <div className="card">
        <h2>{t('nav.routeExplorer')}</h2>
        <p>{t('rte.explorerIntro')}</p>
        <Link className="btn btn--primary" to="/route-explorer">
          {t('rte.openExplorer')}
        </Link>
      </div>
    </section>
  );
}
